import React, { Fragment, FunctionComponent, useEffect, useState } from "react";
import styles from "./App.module.scss";
import { Route, useHistory } from "react-router-dom";
import { thunkGetSiteSettings } from "./umbraco/redux/settings.actions";
import { thunkLoadDictionary } from "components/Dictionary/redux/dictionary.actions";
import { useDispatch, useSelector } from "react-redux";
import combineClasses from "helpers/combine-classes";
import Footer from "../layout/Footer/Footer";
import Header from "../layout/Header/Header";
import MobileMenu from "../layout/Header/components/MobileMenu/MobileMenu";
import UmbracoPage from "./umbraco/pages/UmbracoPage";
import useAppLoading, { AppLoadingState } from "./hooks/useAppLoading";
import useTagManager from "./hooks/useTagManager";
import MegaDropdown from "layout/MegaDropdown/MegaDropdown";
import Modal from "components/Modal/Modal";
import { AnimatePresence, motion } from "framer-motion";
import IconViewer from "components/IconViewer/IconViewer";
import isBrowserContext from "helpers/isBrowserContext";
import Moment from "react-moment";

import "@fontsource/nunito/400.css";
import "@fontsource/nunito/400-italic.css";
import "@fontsource/nunito/900.css";

Moment.globalLocale = "da";

const App: FunctionComponent = () => {
    const dispatch = useDispatch();
    const [animationDelay, setAnimationDelay] = useState(0.2);
    const history = useHistory();
    const settings = useSelector(state => state.settings.content);
    const overlay = useSelector(state => state.overlay);
    const menuOpen = useSelector(state => state.overlay.menuOpen);

    const viewState = useAppLoading(settings);
    const appLoadingClass = viewState !== AppLoadingState.LOADED && isBrowserContext() ? styles.app_loading : undefined;

    useTagManager(history);

    useEffect(() => {
        dispatch(thunkGetSiteSettings(history.location.pathname));
        dispatch(thunkLoadDictionary("/"));
    }, []);

    useEffect(() => {
        if (menuOpen) {
            setAnimationDelay(0);
        }
        else {
            setAnimationDelay(0.2);
        }
    }, [menuOpen]);

    return (
        <Fragment>
            <div className={styles.wrapper}>
                {!isBrowserContext() || viewState === AppLoadingState.LOADED ? (
                    <Header settings={settings} />
                ) : null}

                <main className={combineClasses([styles.content, appLoadingClass])}>
                    <Route path="*" component={UmbracoPage} />
                </main>

                {isBrowserContext() && viewState === AppLoadingState.START_LOADING || viewState === AppLoadingState.STILL_LOADING ? (
                    <div className={styles.app_loading_wrapper}>
                        <IconViewer className={styles.app_loading_icon} name="logo" aspectRatioX={1} aspectRatioY={1} />
                        <span className={styles.app_loading_label}>Timeout; an error properly occured please try again later</span>
                    </div>
                ) : null}

                {!isBrowserContext() || viewState === AppLoadingState.LOADED ? (
                    <Footer settings={settings} />
                ) : null}
            </div>

            <AnimatePresence>
                {overlay.currentOverlay === "MODAL" ? (
                    <motion.div className={styles.modal_wrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                        <Modal modal={overlay.modal} stickyLink={overlay.stickyLink} />
                    </motion.div>
                ) : null}
            </AnimatePresence>

            <AnimatePresence>
                {overlay.currentOverlay === "MEGA_DROPDOWN_MENU" && overlay.menu ? (
                    <motion.div className={styles.megadropdown_wrapper} initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 100 }} transition={{ delay: animationDelay, duration: 0.5 }}>
                        <MegaDropdown menu={overlay.menu} subMenu={overlay.subMenu} />
                    </motion.div>
                ) : null}
            </AnimatePresence>

            {/* TODO: Make a new MOBILE_MENU_CLOSING action to not have it close instant */}
            {overlay.currentOverlay === "MOBILE_MENU" ? (
                <MobileMenu settings={settings} menu={overlay.menu} subMenu={overlay.subMenu} />
            ) : null}
        </Fragment>
    );
};

export default App;